<script setup lang="ts">
import { UiText, UiIcon, UiChip, UiTooltip } from "@shopexperts/shared";
import type { EffectTile, WalletTransactionReference } from "./useWalletTransaction";

withDefaults(
  defineProps<{
    heading: string;
    tiles: EffectTile[];
    references?: WalletTransactionReference[];
  }>(),
  {
    references: () => [],
  },
);
</script>

<template>
  <section class="border-t border-se-gray-200 px-4 py-4">
    <UiText as="h4" variant="p-small" :text="heading" class="font-semibold text-se-dark" />
    <dl class="mt-3 grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-x-0 gap-y-3">
      <div
        v-for="tile in tiles"
        :key="tile.key"
        class="flex items-center gap-2.5 border-se-gray-200 min-[700px]:border-l min-[700px]:pl-4 min-[700px]:first:border-l-0 min-[700px]:first:pl-0"
      >
        <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full" :class="tile.iconClass">
          <UiIcon :name="tile.icon" class="h-4 w-4" />
        </span>
        <div class="flex min-w-0 flex-col">
          <div class="flex items-center gap-1">
            <UiText as="span" variant="small" :text="tile.label" class="text-se-dark" />
            <UiTooltip v-if="tile.tooltip" :content="tile.tooltip">
              <UiIcon name="info-circle" class="h-3.5 w-3.5 shrink-0 text-se-gray-500" />
            </UiTooltip>
          </div>
          <UiText as="span" variant="p" :text="tile.value" class="font-semibold text-se-dark" />
        </div>
      </div>
    </dl>

    <div v-if="references.length" class="mt-4 flex flex-wrap items-center gap-2">
      <template v-for="reference in references" :key="reference.key">
        <RouterLink
          v-if="reference.to"
          :to="reference.to"
          class="inline-flex max-w-full rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-se-dark"
          :aria-label="`Open ${reference.label}`"
        >
          <UiChip :text="reference.label" variant="neutral" size="xs" rounded="full" icon="link-external" />
        </RouterLink>
        <UiChip v-else :text="reference.label" variant="neutral" size="xs" rounded="full" />
      </template>
    </div>
  </section>
</template>
